import { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Check,
  X,
  Sparkles,
  ArrowRight,
  Calculator as CalculatorIcon,
  MessageSquare,
  Brain,
  Zap,
  HelpCircle,
} from 'lucide-react';
import { useThemeStore } from '../../store/theme';

// Pricing tiers
// yearlyPrice is the total annual price (with ~17% discount)
const pricingTiers = [
  {
    id: 'free',
    name: 'Free',
    description: 'Get started with AI employees',
    monthlyPrice: 0,
    yearlyPrice: 0,
    credits: 100,
    features: [
      { text: 'All AI Employees', included: true },
      { text: '100 credits/month', included: true },
      { text: 'Basic integrations', included: true },
      { text: 'Community support', included: true },
      { text: 'Brain AI training', included: false },
      { text: 'Priority support', included: false },
      { text: 'Custom employees', included: false },
      { text: 'API access', included: false },
    ],
    cta: 'Start Free',
    popular: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'Perfect for small teams',
    monthlyPrice: 25,
    yearlyPrice: 250, // $25 * 12 = $300, with discount ~$250/year
    credits: 1500,
    features: [
      { text: 'All AI Employees', included: true },
      { text: '1,500 credits/month', included: true },
      { text: 'All integrations', included: true },
      { text: 'Email support', included: true },
      { text: 'Brain AI training', included: true },
      { text: 'Priority support', included: false },
      { text: 'Custom employees', included: false },
      { text: 'API access', included: false },
    ],
    cta: 'Get Started',
    popular: false,
  },
  {
    id: 'professional',
    name: 'Professional',
    description: 'For growing businesses',
    monthlyPrice: 50,
    yearlyPrice: 500, // $50 * 12 = $600, with discount ~$500/year (~$42/month)
    credits: 3000,
    features: [
      { text: 'All AI Employees', included: true },
      { text: '3,000 credits/month', included: true },
      { text: 'All integrations', included: true },
      { text: 'Priority support', included: true },
      { text: 'Advanced Brain AI', included: true },
      { text: 'Team collaboration', included: true },
      { text: 'Analytics dashboard', included: true },
      { text: 'API access', included: false },
    ],
    cta: 'Get Started',
    popular: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Enterprise-grade features',
    monthlyPrice: 150,
    yearlyPrice: 1500, // $150 * 12 = $1800, with discount ~$1500/year (~$125/month)
    credits: -1, // Unlimited
    features: [
      { text: 'All AI Employees', included: true },
      { text: 'Unlimited credits', included: true },
      { text: 'All integrations', included: true },
      { text: 'Dedicated support', included: true },
      { text: 'Custom Brain training', included: true },
      { text: 'White-label option', included: true },
      { text: 'Custom employees', included: true },
      { text: 'Full API access', included: true },
    ],
    cta: 'Contact Sales',
    popular: false,
  },
];

// Feature comparison data
const comparisonFeatures = [
  { category: 'AI Employees', features: ['Number of employees', 'Custom employee creation', 'Employee customization'] },
  { category: 'Usage', features: ['Monthly messages', 'File uploads', 'Export formats'] },
  { category: 'Brain AI', features: ['Document training', 'Website crawling', 'Custom instructions'] },
  { category: 'Integrations', features: ['Social media', 'Productivity tools', 'eCommerce', 'Custom webhooks'] },
  { category: 'Support', features: ['Response time', 'Dedicated manager', 'Training sessions'] },
];

// Tooltip component
const Tooltip = ({ content }: { content: string }) => (
  <div className="group relative inline-block ml-1">
    <HelpCircle className="w-3.5 h-3.5 text-text-muted cursor-help" />
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-dark-700 border border-white/10 rounded-lg text-xs text-text-secondary whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
      {content}
      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-dark-700" />
    </div>
  </div>
);

// Pricing card
const PricingCard = ({
  tier,
  isYearly,
  index,
}: {
  tier: typeof pricingTiers[0];
  isYearly: boolean;
  index: number;
}) => {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const price = isYearly ? tier.yearlyPrice : tier.monthlyPrice;
  const savings = tier.monthlyPrice * 12 - tier.yearlyPrice;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ delay: index * 0.1 }}
      className={`
        relative rounded-3xl overflow-hidden
        ${tier.popular
          ? isDark
            ? 'bg-gradient-to-b from-accent-purple/20 to-dark-800 border-2 border-accent-purple shadow-glow-sm'
            : 'bg-gradient-to-b from-accent-purple/10 to-white border-2 border-accent-purple shadow-lg'
          : isDark
            ? 'bg-dark-800 border border-white/10'
            : 'bg-white border border-slate-200 shadow-sm'
        }
      `}
    >
      {/* Popular badge */}
      {tier.popular && (
        <div className="absolute top-0 left-0 right-0 bg-gradient-accent py-2 text-center">
          <span className="text-sm font-semibold text-white flex items-center justify-center gap-1">
            <Sparkles className="w-4 h-4" />
            Most Popular
          </span>
        </div>
      )}

      <div className={`p-8 ${tier.popular ? 'pt-16' : ''}`}>
        {/* Header */}
        <div className="text-center mb-8">
          <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{tier.name}</h3>
          <p className={`text-sm mb-6 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{tier.description}</p>

          <div className="flex items-end justify-center gap-1">
            <span className={`text-5xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>${price}</span>
            <span className={`mb-2 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>/{isYearly ? 'year' : 'month'}</span>
          </div>
          {isYearly && tier.monthlyPrice > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-2 text-sm text-accent-green"
            >
              Save ${savings}/year
            </motion.div>
          )}
        </div>

        {/* Quick stats */}
        <div className="mb-8">
          <div className={`text-center p-3 rounded-xl ${isDark ? 'bg-dark-700/50' : 'bg-slate-100'}`}>
            <Zap className="w-5 h-5 mx-auto mb-1 text-accent-cyan" />
            <div className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {tier.credits === -1 ? '∞' : tier.credits >= 1000 ? (tier.credits / 1000).toFixed(1).replace('.0', '') + 'K' : tier.credits}
            </div>
            <div className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Credits/month</div>
          </div>
        </div>

        {/* Features */}
        <ul className="space-y-3 mb-8">
          {tier.features.map((feature) => (
            <li key={feature.text} className="flex items-center gap-3">
              {feature.included ? (
                <div className="w-5 h-5 rounded-full bg-accent-green/20 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-accent-green" />
                </div>
              ) : (
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-dark-700' : 'bg-slate-200'}`}>
                  <X className={`w-3 h-3 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                </div>
              )}
              <span className={`text-sm ${feature.included ? (isDark ? 'text-text-secondary' : 'text-slate-700') : (isDark ? 'text-text-muted' : 'text-slate-400')}`}>
                {feature.text}
              </span>
            </li>
          ))}
        </ul>

        {/* CTA */}
        <Link
          to={tier.id === 'enterprise' ? '/contact' : '/login'}
          className={`
            block w-full py-3.5 rounded-xl text-center font-semibold transition-all
            ${tier.popular
              ? 'bg-gradient-accent text-white hover:shadow-glow-sm hover:scale-[1.02]'
              : isDark
                ? 'bg-dark-700 text-white hover:bg-dark-600 border border-white/5'
                : 'bg-slate-100 text-slate-900 hover:bg-slate-200 border border-slate-200'
            }
          `}
        >
          {tier.cta}
        </Link>
      </div>
    </motion.div>
  );
};

// Calculator component
const PricingCalculatorWidget = () => {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [employees, setEmployees] = useState(8);
  const [messages, setMessages] = useState(10000);
  const [needsBrain, setNeedsBrain] = useState(true);
  const [needsIntegrations, setNeedsIntegrations] = useState(true);

  // Calculate recommended tier
  const getRecommendedTier = () => {
    if (messages > 50000 || employees > 15) return 'enterprise';
    if (messages > 3000 || employees > 8) return 'professional';
    if (messages > 100 || employees > 3 || needsBrain) return 'pro';
    return 'free';
  };

  const recommended = getRecommendedTier();
  const recommendedTier = pricingTiers.find(t => t.id === recommended);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      className="max-w-2xl mx-auto"
    >
      <div className={`p-8 rounded-2xl border ${isDark ? 'bg-dark-800/80 border-white/10' : 'bg-white border-slate-200 shadow-lg'}`}>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-accent-purple/20 flex items-center justify-center">
            <CalculatorIcon className="w-5 h-5 text-accent-purple" />
          </div>
          <div>
            <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Pricing Calculator</h3>
            <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Find your perfect plan</p>
          </div>
        </div>

        <div className="space-y-6 mb-8">
          {/* Employees slider */}
          <div>
            <div className="flex justify-between mb-2">
              <label className={`text-sm ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>AI Employees needed</label>
              <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{employees}</span>
            </div>
            <input
              type="range"
              min="1"
              max="20"
              value={employees}
              onChange={(e) => setEmployees(parseInt(e.target.value))}
              className={`w-full h-2 rounded-full appearance-none cursor-pointer
                       ${isDark ? 'bg-dark-700' : 'bg-slate-200'}
                       [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                       [&::-webkit-slider-thumb]:bg-accent-purple [&::-webkit-slider-thumb]:rounded-full
                       [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-glow-sm`}
            />
            <div className={`flex justify-between text-xs mt-1 ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
              <span>1</span>
              <span>20+</span>
            </div>
          </div>

          {/* Messages slider */}
          <div>
            <div className="flex justify-between mb-2">
              <label className={`text-sm ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Monthly messages</label>
              <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {messages >= 100000 ? '100K+' : (messages / 1000).toFixed(0) + 'K'}
              </span>
            </div>
            <input
              type="range"
              min="500"
              max="100000"
              step="500"
              value={messages}
              onChange={(e) => setMessages(parseInt(e.target.value))}
              className={`w-full h-2 rounded-full appearance-none cursor-pointer
                       ${isDark ? 'bg-dark-700' : 'bg-slate-200'}
                       [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                       [&::-webkit-slider-thumb]:bg-accent-cyan [&::-webkit-slider-thumb]:rounded-full
                       [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-glow-cyan`}
            />
            <div className={`flex justify-between text-xs mt-1 ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
              <span>500</span>
              <span>Unlimited</span>
            </div>
          </div>

          {/* Feature toggles */}
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={needsBrain}
                onChange={(e) => setNeedsBrain(e.target.checked)}
                className={`w-4 h-4 rounded text-accent-purple focus:ring-accent-purple focus:ring-offset-0 ${isDark ? 'border-white/20 bg-dark-700' : 'border-slate-300 bg-white'}`}
              />
              <span className={`text-sm flex items-center ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                <Brain className="w-4 h-4 mr-1 text-accent-purple" />
                Brain AI Training
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={needsIntegrations}
                onChange={(e) => setNeedsIntegrations(e.target.checked)}
                className={`w-4 h-4 rounded text-accent-cyan focus:ring-accent-cyan focus:ring-offset-0 ${isDark ? 'border-white/20 bg-dark-700' : 'border-slate-300 bg-white'}`}
              />
              <span className={`text-sm flex items-center ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                <Zap className="w-4 h-4 mr-1 text-accent-cyan" />
                Full Integrations
              </span>
            </label>
          </div>
        </div>

        {/* Recommendation */}
        <div className={`p-6 rounded-2xl border border-accent-purple/30 ${isDark ? 'bg-dark-700/50' : 'bg-accent-purple/5'}`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className={`text-sm mb-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Recommended Plan</div>
              <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{recommendedTier?.name}</div>
            </div>
            <div className="text-right">
              <div className={`text-sm mb-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Starting at</div>
              <div className="text-3xl font-bold text-accent-purple">
                ${recommendedTier?.monthlyPrice}
                <span className={`text-sm font-normal ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>/mo</span>
              </div>
            </div>
          </div>
          <Link
            to="/login"
            className="block w-full py-3 bg-gradient-accent rounded-xl text-center font-semibold text-white hover:shadow-glow-sm transition-all"
          >
            Start with {recommendedTier?.name}
            <ArrowRight className="w-4 h-4 inline ml-2" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

export default function PricingSection() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const [isYearly, setIsYearly] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);

  return (
    <section ref={ref} id="pricing" className="py-24 lg:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-accent-purple/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-cyan/10 border border-accent-cyan/20 rounded-full text-sm text-accent-cyan mb-6">
            <Sparkles className="w-4 h-4" />
            Simple, Transparent Pricing
          </div>
          <h2 className={`text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Start Free, Scale as You Grow
          </h2>
          <p className={`text-xl max-w-2xl mx-auto ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
            No hidden fees. Cancel anytime. 14-day money-back guarantee.
          </p>
        </motion.div>

        {/* Toggle & Calculator */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.1 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-12"
        >
          {/* Billing toggle */}
          <div className={`flex items-center gap-3 p-1.5 rounded-xl border ${isDark ? 'bg-dark-800 border-white/5' : 'bg-slate-100 border-slate-200'}`}>
            <button
              onClick={() => setIsYearly(false)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                !isYearly ? 'bg-accent-purple text-white' : (isDark ? 'text-text-muted hover:text-white' : 'text-slate-500 hover:text-slate-900')
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsYearly(true)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                isYearly ? 'bg-accent-purple text-white' : (isDark ? 'text-text-muted hover:text-white' : 'text-slate-500 hover:text-slate-900')
              }`}
            >
              Yearly
              <span className="text-xs px-1.5 py-0.5 bg-accent-green/20 text-accent-green rounded">
                -17%
              </span>
            </button>
          </div>

          {/* Calculator toggle */}
          <button
            onClick={() => setShowCalculator(!showCalculator)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              showCalculator
                ? 'bg-accent-cyan/20 text-accent-cyan border border-accent-cyan/30'
                : isDark
                  ? 'bg-dark-800 text-text-secondary hover:text-white border border-white/5'
                  : 'bg-slate-100 text-slate-600 hover:text-slate-900 border border-slate-200'
            }`}
          >
            <CalculatorIcon className="w-4 h-4" />
            Pricing Calculator
          </button>
        </motion.div>

        {/* Calculator */}
        <AnimatePresence>
          {showCalculator && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-12"
            >
              <PricingCalculatorWidget />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {pricingTiers.map((tier, index) => (
            <PricingCard key={tier.id} tier={tier} isYearly={isYearly} index={index} />
          ))}
        </div>

        {/* FAQ Teaser */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4 }}
          className="text-center"
        >
          <p className={`mb-4 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
            Have questions? Check our{' '}
            <a href="#faq" className="text-accent-purple hover:underline">FAQ</a>
            {' '}or{' '}
            <a href="/contact" className="text-accent-purple hover:underline">contact us</a>
          </p>
          <div className={`flex flex-wrap justify-center gap-4 text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
            <span className="flex items-center gap-1">
              <Check className="w-4 h-4 text-accent-green" />
              No credit card required
            </span>
            <span className="flex items-center gap-1">
              <Check className="w-4 h-4 text-accent-green" />
              14-day money-back guarantee
            </span>
            <span className="flex items-center gap-1">
              <Check className="w-4 h-4 text-accent-green" />
              Cancel anytime
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
